/**
 * Payroll Repository
 *
 * Single source of truth for all payroll wizard data operations.
 * Centralizes API calls, implements caching, and provides a clean
 * interface for the XState machine and components.
 *
 * This is the ONLY place that should call httpService for payroll data.
 * All composables should use this repository instead of direct HTTP calls.
 */

import { httpService, type ApiResponse } from '../services/httpService'
import requestDeduplicator from '../utils/requestDeduplicator'
import payrollTransformer, {
  type OpenAdvancesResponse,
  type OpenDeductionsResponse,
  type PendingRefundsResponse,
  type CashRecebimento
} from '../transformers/payroll.transformer'
import { log } from '../utils/DebugLogger'

interface CacheEntry {
  data: unknown
  timestamp: number
}

interface CacheStats {
  repositoryCacheSize: number
  requestDeduplicatorStats: unknown
}

interface BatchAdvanceResult {
  created: unknown[]
  errors: unknown[]
  created_count: number
  error_count: number
}

interface RefundsSummary {
  summary_by_user: unknown[]
  total_refunds: number
  count: number
}

declare global {
  interface Window {
    __payrollRepositoryDebug?: () => void
    __payrollRepositoryStats?: () => CacheStats
    __payrollRepositoryClear?: () => void
  }
}

class PayrollRepository {
  private _cache: Map<string, CacheEntry> = new Map()
  private _defaultCacheTtl: number = 5 * 60 * 1000
  private _debug: boolean = false
  private _name: string = 'PayrollRepository'

  setDebugMode(enabled: boolean): void {
    this._debug = enabled
    requestDeduplicator.setDebugMode(enabled)
  }

  // ============================================
  // PRIVATE METHODS
  // ============================================

  private _generateCacheKey(endpoint: string, params: Record<string, unknown> | string | null = null): string {
    if (params) {
      const paramString = typeof params === 'string'
        ? params
        : JSON.stringify(params)
      return `${endpoint}?${paramString}`
    }
    return endpoint
  }

  private _getFromCache<T = unknown>(key: string): T | null {
    const entry = this._cache.get(key)
    if (!entry) return null

    const now = Date.now()
    if (now - entry.timestamp > this._defaultCacheTtl) {
      this._cache.delete(key)
      return null
    }

    log('PAYROLL_REFACTOR', 'CACHE_METRICS', 'REPOSITORY', 'HIT', {
      cacheKey: key,
      age: now - entry.timestamp,
      ttl: this._defaultCacheTtl
    })

    if (this._debug) {
      log(this._name, '_getFromCache', 'Cache HIT', { key, age: now - entry.timestamp })
    }

    return entry.data as T
  }

  private _setCache(key: string, data: unknown): void {
    this._cache.set(key, {
      data,
      timestamp: Date.now()
    })

    log('PAYROLL_REFACTOR', 'CACHE_METRICS', 'REPOSITORY', 'STORED', {
      cacheKey: key,
      ttl: this._defaultCacheTtl
    })

    if (this._debug) {
      log(this._name, '_setCache', 'Cache SET', { key, dataSize: JSON.stringify(data).length })
    }
  }

  private _clearCache(key: string): void {
    this._cache.delete(key)
    requestDeduplicator.invalidate(key)

    if (this._debug) {
      log(this._name, '_clearCache', 'Cache CLEARED', { key })
    }
  }

  private async _executeRequest<T = unknown>(
    cacheKey: string,
    requestFn: () => Promise<T>,
    requestor: string = 'unknown'
  ): Promise<T> {
    const cached = this._getFromCache<T>(cacheKey)
    if (cached) {
      return cached
    }

    const endpoint = cacheKey.split('?')[0]
    log('PAYROLL_REFACTOR', 'DATA_FLOW', 'REPOSITORY', 'READ_START', {
      endpoint,
      cacheKey,
      cacheStatus: 'MISS'
    })

    const startTime = Date.now()

    try {
      const result = await requestDeduplicator.execute(cacheKey, requestFn, {
        ttl: this._defaultCacheTtl,
        requestor: `${this._name}.${requestor}`
      })

      const duration = Date.now() - startTime

      log('PAYROLL_REFACTOR', 'DATA_FLOW', 'REPOSITORY', 'READ_COMPLETE', {
        endpoint,
        duration: `${duration.toFixed(2)}ms`
      })

      return result as T
    } catch (error: any) {
      log('PAYROLL_REFACTOR', 'ERROR_TRACE', 'REPOSITORY', 'REQUEST_FAILED', {
        endpoint,
        error: error?.message,
        stack: error?.stack
      })
      throw error
    }
  }

  // ============================================
  // EMPLOYEE ADVANCES
  // ============================================

  async getOpenAdvancesForPeriod(periodId: string | number): Promise<OpenAdvancesResponse> {
    const cacheKey = this._generateCacheKey(`/payroll/open_advances_for_period/${periodId}`)

    return this._executeRequest(cacheKey, async () => {
      log(this._name, 'getOpenAdvancesForPeriod', 'Fetching', { periodId })

      log('PAYROLL_REFACTOR', 'TRANSFORM_TRACE', 'API_TO_DOMAIN', 'BEFORE', {
        endpoint: `/payroll/open_advances_for_period/${periodId}`,
        periodId
      })

      const response = await httpService.get<ApiResponse>(`/payroll/open_advances_for_period/${periodId}`)

      if (response.success) {
        const transformed = payrollTransformer.transformOpenAdvancesResponse(response.dados)

        log('PAYROLL_REFACTOR', 'TRANSFORM_TRACE', 'API_TO_DOMAIN', 'AFTER', {
          advancesCount: transformed.advances.length,
          selectedCount: transformed.selected_items?.length || 0,
          totalOpen: transformed.total_open
        })

        log(this._name, 'getOpenAdvancesForPeriod', 'Success', {
          advancesCount: transformed.advances.length,
          totalOpen: transformed.total_open,
          cashRecebimentosCount: transformed.available_cash_recebimentos.length
        })

        return transformed
      } else {
        log('PAYROLL_REFACTOR', 'ERROR_TRACE', 'REPOSITORY', 'API_ERROR', {
          endpoint: `/payroll/open_advances_for_period/${periodId}`,
          error: response.error
        })
        throw new Error(response.error || 'Falha ao buscar adiantamentos abertos')
      }
    }, 'getOpenAdvancesForPeriod')
  }

  async getAvailableCashRecebimentos(periodId: string | number): Promise<CashRecebimento[]> {
    const cacheKey = this._generateCacheKey(`/payroll/available_cash_recebimentos/${periodId}`)

    return this._executeRequest(cacheKey, async () => {
      log(this._name, 'getAvailableCashRecebimentos', 'Fetching', { periodId })

      const response = await httpService.get<ApiResponse>(`/payroll/available_cash_recebimentos/${periodId}`)

      if (response.success) {
        const transformed = payrollTransformer.transformCashRecebimentosFromApi(response.dados || [])

        log(this._name, 'getAvailableCashRecebimentos', 'Success', {
          count: transformed.length
        })

        return transformed
      } else {
        throw new Error(response.error || 'Falha ao buscar recebimentos em dinheiro')
      }
    }, 'getAvailableCashRecebimentos')
  }

  async createCashAdvance(
    periodId: string | number,
    recebimentoId: string | number,
    franchiseId: string | number
  ): Promise<string | number | null> {
    log(this._name, 'createCashAdvance', 'Creating', {
      periodId,
      recebimentoId,
      franchiseId
    })

    const response = await httpService.post<ApiResponse & { advance_id?: string | number | null }>('/payroll/create_cash_advance', {
      period_id: periodId,
      recebimento_id: recebimentoId,
      franchise_id: franchiseId
    })

    this._clearCache(`/payroll/open_advances_for_period/${periodId}`)

    if (response.success) {
      log(this._name, 'createCashAdvance', 'Success', {
        advanceId: response.advance_id
      })
      return response.advance_id ?? null
    } else {
      throw new Error(response.error || 'Falha ao criar adiantamento de dinheiro')
    }
  }

  async createCashAdvancesBatch(
    periodId: string | number,
    recebimentoIds: Array<string | number>,
    franchiseId: string | number
  ): Promise<BatchAdvanceResult> {
    log(this._name, 'createCashAdvancesBatch', 'Creating batch', {
      periodId,
      count: recebimentoIds.length,
      franchiseId
    })

    const response = await httpService.post<ApiResponse<BatchAdvanceResult>>('/payroll/create_cash_advances_batch', {
      period_id: periodId,
      recebimento_ids: recebimentoIds,
      franchise_id: franchiseId
    })

    this._clearCache(`/payroll/open_advances_for_period/${periodId}`)

    if (response.success) {
      log(this._name, 'createCashAdvancesBatch', 'Success', {
        createdCount: response.dados?.created_count || 0,
        errorCount: response.dados?.error_count || 0
      })
      return response.dados || { created: [], errors: [], created_count: 0, error_count: 0 }
    } else {
      throw new Error(response.error || 'Falha ao criar adiantamentos em lote')
    }
  }

  async processAdvanceDiscounts(periodId: string | number, discounts: unknown[]): Promise<unknown> {
    log(this._name, 'processAdvanceDiscounts', 'Processing', {
      periodId,
      discountCount: discounts.length
    })

    const payload = {
      period_id: periodId,
      discounts: payrollTransformer.transformSelectedDiscountsToApi(discounts, 'advance_id')
    }

    const response = await httpService.post<ApiResponse>('/payroll/process_advance_discounts', payload)

    this._clearCache(`/payroll/open_advances_for_period/${periodId}`)

    if (response.success) {
      log(this._name, 'processAdvanceDiscounts', 'Success')
      return response.dados
    } else {
      throw new Error(response.error || 'Falha ao processar descontos')
    }
  }

  // ============================================
  // EMPLOYEE DEDUCTIONS
  // ============================================

  async getOpenDeductionsForPeriod(periodId: string | number): Promise<OpenDeductionsResponse> {
    const cacheKey = this._generateCacheKey(`/payroll/open_deductions_for_period/${periodId}`)

    return this._executeRequest(cacheKey, async () => {
      log(this._name, 'getOpenDeductionsForPeriod', 'Fetching', { periodId })

      log('PAYROLL_REFACTOR', 'TRANSFORM_TRACE', 'API_TO_DOMAIN', 'BEFORE', {
        endpoint: `/payroll/open_deductions_for_period/${periodId}`,
        periodId
      })

      const response = await httpService.get<ApiResponse>(`/payroll/open_deductions_for_period/${periodId}`)

      if (response.success) {
        const transformed = payrollTransformer.transformOpenDeductionsResponse(response.dados)

        log('PAYROLL_REFACTOR', 'TRANSFORM_TRACE', 'API_TO_DOMAIN', 'AFTER', {
          deductionsCount: transformed.deductions.length,
          selectedCount: transformed.selected_items?.length || 0,
          totalOpen: transformed.total_open
        })

        log(this._name, 'getOpenDeductionsForPeriod', 'Success', {
          deductionsCount: transformed.deductions.length,
          totalOpen: transformed.total_open
        })

        return transformed
      } else {
        log('PAYROLL_REFACTOR', 'ERROR_TRACE', 'REPOSITORY', 'API_ERROR', {
          endpoint: `/payroll/open_deductions_for_period/${periodId}`,
          error: response.error
        })
        throw new Error(response.error || 'Falha ao buscar deduções abertas')
      }
    }, 'getOpenDeductionsForPeriod')
  }

  async processDeductionDiscounts(periodId: string | number, discounts: unknown[]): Promise<unknown> {
    log(this._name, 'processDeductionDiscounts', 'Processing', {
      periodId,
      discountCount: discounts.length
    })

    const payload = {
      period_id: periodId,
      discounts: payrollTransformer.transformSelectedDiscountsToApi(discounts, 'deduction_id')
    }

    const response = await httpService.post<ApiResponse>('/payroll/process_deduction_discounts', payload)

    this._clearCache(`/payroll/open_deductions_for_period/${periodId}`)

    if (response.success) {
      log(this._name, 'processDeductionDiscounts', 'Success')
      return response.dados
    } else {
      throw new Error(response.error || 'Falha ao processar descontos')
    }
  }

  // ============================================
  // PAYROLL REFUNDS
  // ============================================

  async getPendingRefundsForPeriod(periodId: string | number): Promise<PendingRefundsResponse> {
    const cacheKey = this._generateCacheKey(`/payroll/pending_refunds/${periodId}`)

    return this._executeRequest(cacheKey, async () => {
      log(this._name, 'getPendingRefundsForPeriod', 'Fetching', { periodId })

      log('PAYROLL_REFACTOR', 'TRANSFORM_TRACE', 'API_TO_DOMAIN', 'BEFORE', {
        endpoint: `/payroll/pending_refunds/${periodId}`,
        periodId
      })

      const response = await httpService.get<ApiResponse>(`/payroll/pending_refunds/${periodId}`)

      if (response.success) {
        const transformed = payrollTransformer.transformPendingRefundsResponse(response.dados)

        log('PAYROLL_REFACTOR', 'TRANSFORM_TRACE', 'API_TO_DOMAIN', 'AFTER', {
          refundsCount: transformed.refunds.length,
          totalPending: transformed.total_pending
        })

        log(this._name, 'getPendingRefundsForPeriod', 'Success', {
          refundsCount: transformed.refunds.length,
          totalPending: transformed.total_pending
        })

        return transformed
      } else {
        log('PAYROLL_REFACTOR', 'ERROR_TRACE', 'REPOSITORY', 'API_ERROR', {
          endpoint: `/payroll/pending_refunds/${periodId}`,
          error: response.error
        })
        throw new Error(response.error || 'Falha ao buscar reembolsos pendentes')
      }
    }, 'getPendingRefundsForPeriod')
  }

  async getRefundsSummary(periodId: string | number): Promise<RefundsSummary> {
    const cacheKey = this._generateCacheKey(`/payroll/refunds_summary/${periodId}`)

    return this._executeRequest(cacheKey, async () => {
      log(this._name, 'getRefundsSummary', 'Fetching', { periodId })

      const response = await httpService.get<ApiResponse<RefundsSummary>>(`/payroll/refunds_summary/${periodId}`)

      if (response.success) {
        log(this._name, 'getRefundsSummary', 'Success')
        return response.dados || { summary_by_user: [], total_refunds: 0, count: 0 }
      } else {
        throw new Error(response.error || 'Falha ao buscar resumo de reembolsos')
      }
    }, 'getRefundsSummary')
  }

  async processRefundsBatch(periodId: string | number, decisions: unknown[]): Promise<unknown> {
    log(this._name, 'processRefundsBatch', 'Processing batch', {
      periodId,
      decisionCount: decisions.length
    })

    const payload = {
      period_id: periodId,
      decisions: payrollTransformer.transformRefundDecisionsToApi(decisions)
    }

    const response = await httpService.post<ApiResponse<{ accepted?: unknown[]; declined?: unknown[]; errors?: unknown[] }>>('/payroll/process_refunds_batch', payload)

    this._clearCache(`/payroll/pending_refunds/${periodId}`)
    this._clearCache(`/payroll/refunds_summary/${periodId}`)

    if (response.success) {
      log(this._name, 'processRefundsBatch', 'Success', {
        acceptedCount: response.dados?.accepted?.length || 0,
        declinedCount: response.dados?.declined?.length || 0,
        errorCount: response.dados?.errors?.length || 0
      })
      return response.dados
    } else {
      throw new Error(response.error || 'Falha ao processar reembolsos em lote')
    }
  }

  // ============================================
  // CACHE MANAGEMENT
  // ============================================

  invalidatePeriodCache(periodId: string | number): void {
    log(this._name, 'invalidatePeriodCache', 'Invalidating', { periodId })

    this._clearCache(`/payroll/open_advances_for_period/${periodId}`)
    this._clearCache(`/payroll/open_deductions_for_period/${periodId}`)
    this._clearCache(`/payroll/pending_refunds/${periodId}`)
    this._clearCache(`/payroll/refunds_summary/${periodId}`)
    this._clearCache(`/payroll/available_cash_recebimentos/${periodId}`)
  }

  invalidatePattern(pattern: string): void {
    log(this._name, 'invalidatePattern', 'Invalidating', { pattern })

    const regex = new RegExp(pattern)
    for (const key of this._cache.keys()) {
      if (regex.test(key)) {
        this._clearCache(key)
      }
    }
  }

  clearCache(): void {
    log(this._name, 'clearCache', 'Clearing all cache')

    this._cache.clear()
    requestDeduplicator.clear()
  }

  getCacheStats(): CacheStats {
    return {
      repositoryCacheSize: this._cache.size,
      requestDeduplicatorStats: requestDeduplicator.getStats()
    }
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

const payrollRepository = new PayrollRepository()

window.__payrollRepositoryDebug = () => {
  payrollRepository.setDebugMode(true)
  log('PayrollRepository', 'window.__payrollRepositoryDebug', 'Debug mode enabled')
}

window.__payrollRepositoryStats = () => {
  return payrollRepository.getCacheStats()
}

window.__payrollRepositoryClear = () => {
  payrollRepository.clearCache()
}

export default payrollRepository
export { PayrollRepository }
